import type { Message } from '../../types';

export type FailureReasonKind =
  | 'rate_limit'
  | 'auth_error'
  | 'timeout'
  | 'network_drop'
  | 'server_error'
  | 'model_gone'
  | 'output_limit'
  | 'truncated'
  | 'content_filter'
  | 'unknown';

export interface FailureReason {
  kind: FailureReasonKind;
  /** Short label for the error header, e.g. "额度超限". */
  label: string;
  /** What the user can actually do about it. */
  hint: string;
}

const LABELS: Record<FailureReasonKind, { zh: [string, string]; en: [string, string] }> = {
  rate_limit: {
    zh: ['额度或频率超限', '模型侧限流。稍等片刻再试，或在模型菜单换一个供应商。'],
    en: ['Rate limited', 'The model provider is throttling. Wait a moment or switch provider in the model menu.'],
  },
  // Never the end of a turn on its own: the generator lowers the budget and
  // asks the same model again. It reaches here only if every model in the pool
  // refused every budget down to the floor.
  output_limit: {
    zh: ['输出长度受限', '模型拒绝了本次请求的输出长度上限，且降到下限后仍不接受。换一个供应商，或缩小本轮的生成范围。'],
    en: ['Output budget refused', 'The provider rejected the requested output length, and still refused after backing off. Switch provider, or narrow what this turn asks for.'],
  },
  auth_error: {
    zh: ['密钥无效', '当前供应商的 API Key 被拒绝。请到设置中检查密钥。'],
    en: ['Invalid credentials', 'The provider rejected the API key. Check it in Settings.'],
  },
  timeout: {
    zh: ['模型超时', '模型在时限内没有返回。重试通常有效；也可切到 FAST 模式。'],
    en: ['Model timeout', 'The model did not respond in time. Retrying usually works; FAST mode is an alternative.'],
  },
  network_drop: {
    zh: ['连接中断', '与模型的连接中途断开。请检查网络后重试。'],
    en: ['Connection dropped', 'The connection to the model was interrupted. Check your network and retry.'],
  },
  server_error: {
    zh: ['模型服务异常', '供应商返回服务端错误。重试或换一个模型。'],
    en: ['Provider error', 'The provider returned a server error. Retry or switch model.'],
  },
  model_gone: {
    zh: ['模型不可用', '所选模型已下线或无权限。请在模型菜单中另选一个。'],
    en: ['Model unavailable', 'The selected model is retired or not permitted. Pick another one.'],
  },
  truncated: {
    zh: ['输出被截断', '本轮达到输出上限。可以缩小需求范围，或分块生成。'],
    en: ['Output truncated', 'The output limit was reached. Narrow the scope or generate in parts.'],
  },
  content_filter: {
    zh: ['内容被拦截', '模型侧的内容策略拒绝了本次请求。调整措辞后重试。'],
    en: ['Content filtered', 'The provider blocked this request. Rephrase and retry.'],
  },
  unknown: {
    zh: ['生成失败', '未能取得模型输出。可以直接重试。'],
    en: ['Generation failed', 'No model output was produced. Retrying is safe.'],
  },
};

type ModelAttempt = NonNullable<NonNullable<Message['generationMetrics']>['modelAttempts']>[number];

function attemptKind(attempt: ModelAttempt): string {
  if (attempt.errorCategory && attempt.errorCategory !== 'unknown') return attempt.errorCategory;
  return attempt.timedOut ? 'timeout' : 'unknown';
}

/**
 * The failed attempt that speaks for the turn: the latest one of the most
 * common kind, among those after the last attempt that succeeded — what an
 * earlier step recovered from is not why the turn failed.
 *
 * It used to be simply the latest. On 2026-09-21 a planning turn ran out of
 * models, two of them cut off while still reasoning, and the label read
 * 「额度或频率超限」 because the last one asked happened to answer 429.
 */
function representativeFailure(attempts: ModelAttempt[]): ModelAttempt | undefined {
  const lastSuccess = attempts.map(attempt => attempt.status).lastIndexOf('success');
  const failed = attempts.slice(lastSuccess + 1).filter(attempt => attempt.status === 'error');
  const counts = new Map<string, number>();
  for (const attempt of failed) counts.set(attemptKind(attempt), (counts.get(attemptKind(attempt)) || 0) + 1);
  let chosen: ModelAttempt | undefined;
  for (const attempt of failed) {
    if (!chosen || counts.get(attemptKind(attempt))! >= counts.get(attemptKind(chosen))!) chosen = attempt;
  }
  return chosen;
}

/**
 * Why this turn produced nothing.
 *
 * Reads the real per-attempt diagnostics instead of assuming a network error —
 * a wrong guess here sends the user to check their wifi when the real cause was
 * a rate limit or an expired key.
 */
export function getFailureReason(message: Message, isChinese: boolean): FailureReason {
  const attempts = message.generationMetrics?.modelAttempts || [];
  const failed = representativeFailure(attempts);

  let kind: FailureReasonKind = 'unknown';
  if (failed?.errorCategory && failed.errorCategory !== 'unknown') {
    kind = failed.errorCategory;
  } else if (failed?.timedOut) {
    kind = 'timeout';
  } else if (
    // An empty reply that ended on `length` was not cut off mid-answer: there
    // was no answer, the budget went on reasoning. "Narrow the scope" is the
    // wrong advice for that, so it stays `unknown` — retrying is the advice.
    failed?.errorDetail !== 'empty_completion'
    && attempts.some(attempt => attempt.truncated || attempt.finishReason?.toLowerCase() === 'length')
  ) {
    kind = 'truncated';
  } else if (attempts.some(attempt => /content_filter|safety/i.test(attempt.finishReason || ''))) {
    kind = 'content_filter';
  }

  const [label, hint] = isChinese ? LABELS[kind].zh : LABELS[kind].en;
  return { kind, label, hint };
}
